// src/components/ConsultantDashboard/ConsultantChatView.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Avatar,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline,
  PeopleAlt as PeopleAltIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Whatshot as WhatshotIcon,
  AccessTime as AccessTimeIcon
} from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
  increment,
} from "firebase/firestore";

// Imports from other files/folders
import { db as firestoreDb } from "../../firebase";
import api from "../../axiosConfig";
import OmniCommandBar from "../OmniCommandBar";
import { CustomerDetailItem, getTravelPriority } from "./ConsultantUtils";

const GLOBAL_APP_ID = "1:240171385312:web:16ba555d363cf85bc62b85";
const QUOTE_URL = "https://www.abisiniya.com/office/quotes/create";

const CHAT_TIPS = [
  "Use /urgent to mark this chat as urgent so it stands out for the team.",
  "Use /handoff to hand this chat to another consultant or the Marketing team.",
  "Use /followup tomorrow 9am to create a follow-up reminder note in the inquiry.",
  "Type '/email_followup' to log that you’ll email the customer later.",
  // ✅ removed: ask tip
  "Watch the emoji indicator at the top-right – it tracks the customer's mood automatically.",
];

// --- HELPER: Relative Time ---
const getRelativeTime = (date) => {
  if (!date) return "";
  const validDate = date.toDate ? date.toDate() : new Date(date);

  const now = new Date();
  const diff = now - validDate;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

export default function ConsultantInquiryChatView({ inquiry, onBack, consultant, onMarkClosed }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [messageFetchError, setMessageFetchError] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isCustomerDetailsVisible, setIsCustomerDetailsVisible] = useState(false);

  const [presenceSnapshot, setPresenceSnapshot] = useState(null);
  const [computedOnline, setComputedOnline] = useState(false);
  const [displayLastSeen, setDisplayLastSeen] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const messagesEndRef = useRef(null);

  const [currentMood, setCurrentMood] = useState({ label: "neutral", score: 0 });
  const [isUrgent, setIsUrgent] = useState(
    !!(inquiry?.isUrgent || inquiry?.priority === "urgent")
  );

  const [showTips, setShowTips] = useState(true);
  const [tipIndex, setTipIndex] = useState(0);
  const [handoffDialogOpen, setHandoffDialogOpen] = useState(false);
  const [availableConsultants, setAvailableConsultants] = useState([]);
  const [marketingUsers, setMarketingUsers] = useState([]);

  const isChatClosed = ["closed_by_consultant", "closed_by_user", "closed_by_provider"].includes(
    inquiry?.status
  );

  const priorityData = useMemo(() => {
    return getTravelPriority(inquiry?.formData?.travelDate);
  }, [inquiry]);

  const isHot = !isChatClosed && priorityData.level === "hot";

  const markInquiryAsRead = useCallback(async () => {
    if (!inquiry?.id || !consultant?.uid) return;

    const unreadCount = Number(inquiry.consultantUnreadCount || 0);
    if (unreadCount <= 0) return;

    const appIdToUse = inquiry.appId || GLOBAL_APP_ID;
    const userInquiryDocId = inquiry.originalInquiryId || inquiry.id;
    const readUpdates = {
      consultantUnreadCount: 0,
      lastReadByConsultantAt: serverTimestamp(),
    };

    const writes = [updateDoc(doc(firestoreDb, "allInquiries", inquiry.id), readUpdates)];

    if (inquiry.userId && userInquiryDocId) {
      writes.push(
        updateDoc(
          doc(
            firestoreDb,
            `artifacts/${appIdToUse}/users/${inquiry.userId}/inquiries/${userInquiryDocId}`
          ),
          readUpdates
        )
      );
    }

    const results = await Promise.allSettled(writes);
    results.forEach((result) => {
      if (result.status === "rejected") {
        console.warn("Could not clear unread count:", result.reason);
      }
    });
  }, [consultant?.uid, inquiry?.appId, inquiry?.consultantUnreadCount, inquiry?.id, inquiry?.originalInquiryId, inquiry?.userId]);

  useEffect(() => {
    markInquiryAsRead().catch((err) => {
      console.warn("Could not mark messages as read:", err);
    });
  }, [markInquiryAsRead]);

  // --- 2. LISTEN FOR CUSTOMER PRESENCE ---
  useEffect(() => {
    if (!inquiry?.userId) return;

    const presenceRef = doc(firestoreDb, "userPresence", inquiry.userId);
    const unsubscribe = onSnapshot(presenceRef, (docSnap) => {
      if (docSnap.exists()) {
        setPresenceSnapshot(docSnap.data());
      } else {
        setPresenceSnapshot(null);
      }
    });
    return () => unsubscribe();
  }, [inquiry?.userId]);

  // --- 3. CALCULATE LIVENESS (STRICTEST FIX) ---
  useEffect(() => {
    const calculateStatus = () => {
      const activeInquiryId = inquiry.originalInquiryId || inquiry.id;

      // A. Handle Missing Data
      if (!presenceSnapshot) {
        setComputedOnline(false);
        // Fallback: If no presence data, do NOT update displayLastSeen to "now".
        if (!displayLastSeen) {
          const lastInteraction = inquiry.lastReadByUserAt || inquiry.updatedAt;
          if (lastInteraction) {
            setDisplayLastSeen(lastInteraction.toDate ? lastInteraction.toDate() : new Date(lastInteraction));
          }
        }
        return;
      }

      const { isOnline, lastSeen, currentInquiryId } = presenceSnapshot;

      // B. ULTRA-STRICT SCOPE CHECK
      if (!currentInquiryId || String(currentInquiryId) !== String(activeInquiryId)) {
        setComputedOnline(false);
        return;
      }

      // C. If Inquiry IDs Match, we trust the data and update the timestamp
      const lastSeenDate = lastSeen?.toDate ? lastSeen.toDate() : new Date(lastSeen || 0);
      setDisplayLastSeen(lastSeenDate);

      if (!isOnline) {
        setComputedOnline(false);
        return;
      }

      const now = new Date();
      const diffSeconds = (now - lastSeenDate) / 1000;

      if (diffSeconds > 20) {
        setComputedOnline(false);
      } else {
        setComputedOnline(true);
      }
    };

    calculateStatus();
    const interval = setInterval(calculateStatus, 5000);

    return () => clearInterval(interval);
  }, [presenceSnapshot, inquiry, displayLastSeen]);

  // FETCH COLLEAGUES
  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      try {
        const res = await api.get("/consultant/users");
        if (cancelled) return;

        const raw =
          Array.isArray(res.data?.data) && res.data.data.length
            ? res.data.data
            : Array.isArray(res.data)
              ? res.data
              : [];

        const consultantList = raw.filter(
          (u) => String(u.role || "").toLowerCase() === "consultant"
        );

        const marketingList = raw.filter(
          (u) => String(u.role || "").toLowerCase() === "marketing"
        );

        const myId = consultant?.uid ? String(consultant.uid) : "";
        const others = consultantList.filter((c) => String(c.id) !== myId && String(c.uid) !== myId);

        setAvailableConsultants(others);
        setMarketingUsers(marketingList);

      } catch (err) {
        if (cancelled) {
          return;
        }

        if (err?.response?.status === 403) {
          try {
            const fallbackRes = await api.get("/users");
            if (cancelled) return;

            const raw =
              Array.isArray(fallbackRes.data?.data) && fallbackRes.data.data.length
                ? fallbackRes.data.data
                : Array.isArray(fallbackRes.data)
                  ? fallbackRes.data
                  : [];

            const consultantList = raw.filter(
              (u) => String(u.role || "").toLowerCase() === "consultant"
            );

            const marketingList = raw.filter(
              (u) => String(u.role || "").toLowerCase() === "marketing"
            );

            const myId = consultant?.uid ? String(consultant.uid) : "";
            const others = consultantList.filter((c) => String(c.id) !== myId && String(c.uid) !== myId);

            setAvailableConsultants(others);
            setMarketingUsers(marketingList);
            return;
          } catch (fallbackErr) {
            console.error("Error loading users", fallbackErr);
            return;
          }
        }

        console.error("Error loading users", err);
      }
    };

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [consultant]);

  const isServiceProvider = inquiry?.inquiryUserType === "service_provider_v2";

  const consultantDisplayName =
    consultant?.name ||
    consultant?.displayName ||
    consultant?.email?.split("@")[0] ||
    "Consultant";

  const resolveInquiryIdentifier = useCallback(() => {
    return inquiry?.sqlId || inquiry?.firestore_document_id || inquiry?.id || null;
  }, [inquiry]);

  const syncInquiryMutation = useCallback(async (pathBuilder, payload = null) => {
    const identifier = resolveInquiryIdentifier();
    if (!identifier) {
      return false;
    }

    try {
      await api.post(pathBuilder(identifier), payload || {});
      return true;
    } catch (error) {
      console.warn("Consultant inquiry action sync failed", error);
      return false;
    }
  }, [resolveInquiryIdentifier]);

  const syncInquiryActivity = useCallback(async (eventKey, options = {}) => {
    return syncInquiryMutation(
      (identifier) => `/api/inquiries/${identifier}/activity`,
      {
        event_key: eventKey,
        channel: options.channel || inquiry?.channel || inquiry?.origin || "web",
        status: options.status,
        quantity: options.quantity,
        metadata: {
          source: "consultant_dashboard",
          firestore_inquiry_id: inquiry?.id,
          ...(options.metadata || {}),
        },
      }
    );
  }, [inquiry?.channel, inquiry?.id, inquiry?.origin, syncInquiryMutation]);

  const isConsultantMessage = (msg) => {
    if (!msg) return false;

    const sender = (msg.sender || "").toString().toLowerCase();
    const senderRole = (msg.senderRole || msg.role || msg.sender_type || "").toString().toLowerCase();
    const senderId = msg.senderId || msg.consultantId;
    const senderEmail = msg.senderEmail ? msg.senderEmail.toString().toLowerCase() : null;

    const myUid = consultant?.uid ? String(consultant.uid) : null;
    const myEmail = consultant?.email ? consultant.email.toString().toLowerCase() : null;

    if (senderId && myUid && String(senderId) === myUid) return true;
    if (myEmail && senderEmail && myEmail === senderEmail) return true;
    if (sender && ["consultant", "agent", "support"].includes(sender)) return true;
    if (senderRole && (senderRole.includes("consultant") || senderRole.includes("agent"))) return true;

    return false;
  };

  useEffect(() => {
    setIsUrgent(!!(inquiry?.isUrgent || inquiry?.priority === "urgent"));
  }, [inquiry]);

  useEffect(() => {
    setShowTips(true);
    setTipIndex(0);
  }, [inquiry?.id]);

  // --------- LOAD MESSAGES ----------
  useEffect(() => {
    if (!inquiry || !inquiry.userId || !(inquiry.originalInquiryId || inquiry.id)) {
      setLoadingMessages(false);
      setMessages([]);
      setMessageFetchError(null);
      return;
    }

    setLoadingMessages(true);
    setMessageFetchError(null);

    const appIdToUse = inquiry.appId || GLOBAL_APP_ID;
    const userInquiryDocId = inquiry.originalInquiryId || inquiry.id;
    const messagesPath = `artifacts/${appIdToUse}/users/${inquiry.userId}/inquiries/${userInquiryDocId}/messages`;

    const qMsgs = query(collection(firestoreDb, messagesPath), orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(
      qMsgs,
      (snapshot) => {
        setMessages(snapshot.docs.map((docL) => ({ id: docL.id, ...docL.data() })));
        setLoadingMessages(false);
      },
      (error) => {
        setMessageFetchError(`Failed to load messages: ${error.message}.`);
        setLoadingMessages(false);
      }
    );

    return () => unsubscribe();
  }, [inquiry]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (text) => {
    const newMessage = (text || "").trim();
    if (
      !newMessage ||
      !inquiry ||
      !consultant ||
      !consultant.uid ||
      sendingMessage ||
      isChatClosed
    ) {
      return;
    }

    setSendingMessage(true);

    const appIdToUse = inquiry.appId || GLOBAL_APP_ID;
    const userInquiryDocId = inquiry.originalInquiryId || inquiry.id;
    const messagesPath = `artifacts/${appIdToUse}/users/${inquiry.userId}/inquiries/${userInquiryDocId}/messages`;

    try {
      await addDoc(collection(firestoreDb, messagesPath), {
        text: newMessage,
        sender: "consultant",
        senderId: consultant.uid,
        senderName: consultantDisplayName,
        timestamp: serverTimestamp(),
      });

      await updateDoc(doc(firestoreDb, "allInquiries", inquiry.id), {
        lastMessageSnippet: newMessage.substring(0, 50),
        lastMessageBy: "consultant",
        status: "pending_user_response",
        updatedAt: serverTimestamp(),
        consultantUnreadCount: 0,
        userUnreadCount: increment(1),
      });
      await syncInquiryActivity("omnichannel.reply_sent", {
        metadata: {
          body_preview: newMessage.substring(0, 120),
        },
      });
    } catch (error) {
      console.error("Error sending message: ", error);
      setMessageFetchError("Failed to send message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  const logSystemAction = async (actionType, meta = {}, extra = {}) => {
    if (!inquiry || !inquiry.id || !consultant || !consultant.uid) return;

    const appIdToUse = inquiry.appId || GLOBAL_APP_ID;
    const userInquiryDocId = inquiry.originalInquiryId || inquiry.id;
    const messagesPath = `artifacts/${appIdToUse}/users/${inquiry.userId}/inquiries/${userInquiryDocId}/messages`;

    try {
      await addDoc(collection(firestoreDb, messagesPath), {
        sender: "system",
        senderId: consultant.uid,
        senderName: consultantDisplayName,
        text: extra.text || "",
        actionType,
        meta,
        timestamp: serverTimestamp(),
      });
    } catch (err) {
      console.error("Error logging system action:", err);
    }
  };

  const markAsUrgent = async ({ automatic = false } = {}) => {
    if (!inquiry || !inquiry.id) return;
    if (isUrgent || inquiry.isUrgent || inquiry.priority === "urgent") return;

    try {
      await updateDoc(doc(firestoreDb, "allInquiries", inquiry.id), {
        isUrgent: true,
        priority: "urgent",
        prioritySource: automatic ? "automatic" : "manual",
        urgentMarkedAt: serverTimestamp(),
        urgentMarkedBy: consultant?.uid || consultant?.email || "system",
        urgentMarkedAutomatically: automatic,
      });
      setIsUrgent(true);
      await syncInquiryMutation((identifier) => `/api/inquiries/${identifier}/mark-urgent`);

      await logSystemAction(
        "urgent",
        { automatic },
        {
          text: automatic
            ? `${consultantDisplayName} marked this inquiry as urgent (auto-detected).`
            : `${consultantDisplayName} marked this inquiry as urgent.`,
        }
      );
    } catch (err) {
      console.error("Error marking inquiry urgent:", err);
    }
  };

  const handleHandoff = async (targetMode, targetConsultant = null) => {
    if (!inquiry || !inquiry.id || !consultant) return;

    try {
      const inquiryRef = doc(firestoreDb, "allInquiries", inquiry.id);

      let updates = {
        handedOffBy: consultant.uid || consultant.email,
        handedOffAt: serverTimestamp(),
      };

      let systemLogText = "";

      if (targetMode === "marketing") {
        updates.consultantId = null;
        updates.consultantName = "Marketing Team";
        updates.status = "pending_marketing";
        updates.handoffTarget = "marketing";
        systemLogText = `${consultantDisplayName} escalated this inquiry to the Marketing Team.`;
      }
      else if (targetMode === "specific" && targetConsultant) {
        updates.consultantId = String(targetConsultant.id);
        updates.consultantName = targetConsultant.name;
        updates.status = "assigned";
        updates.handoffTarget = "peer";
        systemLogText = `${consultantDisplayName} handed this inquiry directly to ${targetConsultant.name}.`;
      } else {
        return;
      }

      await updateDoc(inquiryRef, updates);
      await syncInquiryMutation(
        (identifier) => `/api/inquiries/${identifier}/handoff`,
        targetMode === "marketing"
          ? {
              targetTeam: "marketing",
              reason: "Consultant handoff to marketing",
            }
          : {
              toUserId: targetConsultant?.sqlId || targetConsultant?.id,
              reason:
                String(targetConsultant?.role || "").toLowerCase() === "marketing"
                  ? "Consultant handoff to marketing"
                  : "Consultant handoff to colleague",
            }
      );

      await logSystemAction(
        "handoff",
        { target: targetMode },
        { text: systemLogText }
      );

      setSnackbarMessage("Handoff successful.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setTimeout(() => onBack(), 1000);

    } catch (err) {
      console.error("Error handing off inquiry:", err);
      setSnackbarMessage("Failed to hand off inquiry.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleFollowup = async (noteRaw) => {
    if (!inquiry || !inquiry.id) return;

    const note = (noteRaw || "").trim();
    const isClaimed = !!inquiry.consultantId;

    const followupFromEmail = isClaimed
      ? consultant?.email || "abisiniyatravels@gmail.com"
      : "abisiniyatravels@gmail.com";

    try {
      await updateDoc(doc(firestoreDb, "allInquiries", inquiry.id), {
        followupRequested: true,
        followupNote: note || null,
        followupRequestedAt: serverTimestamp(),
        followupRequestedBy: consultant?.uid || consultant?.email || "system",
        followupFromEmail,
      });
      await syncInquiryActivity("omnichannel.follow_up_created", {
        metadata: {
          note,
          followupFromEmail,
        },
      });

      const preview =
        note.length > 120 ? `${note.slice(0, 117)}...` : note || "No note";

      await logSystemAction(
        "followup",
        { note, followupFromEmail },
        {
          text: `${consultantDisplayName} recorded a follow-up: “${preview}”`,
        }
      );

      setSnackbarMessage("Follow-up scheduled.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

    } catch (err) {
      console.error("Error creating follow-up:", err);
      setSnackbarMessage("Failed to schedule follow-up.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleOmniExecute = (kind, payload) => {
    if (isChatClosed) return;
    if (!payload) return;

    if (kind === "message") {
      const msg = (payload || "").trim();
      if (!msg) return;
      handleSendMessage(msg);
      return;
    }

    if (kind === "command") {
      const trimmed = (payload || "").trim();
      if (!trimmed) return;

      const lowerFull = trimmed.toLowerCase();
      const normalizedLoose = lowerFull.replace(/[\s_]+/g, "");

      const isUrgentCmd = normalizedLoose.includes("/urgent");
      const isHandoffCmd = normalizedLoose.includes("/handoff");
      const isFollowupCmd = normalizedLoose.includes("/followup");
      const isEmailFollowupCmd = normalizedLoose.includes("/emailfollowup");
      const isSendQuoteCmd = normalizedLoose.includes("/sendquote") || normalizedLoose.includes("/send");
      // ✅ removed:
      // const isAskNowCmd = normalizedLoose.includes("/ask");
      const isGhostCmd = normalizedLoose.includes("/ghost");

      if (isUrgentCmd) {
        markAsUrgent({ automatic: false });
        logSystemAction("urgent_manual", { raw: trimmed });
        return;
      }

      if (isHandoffCmd) {
        setHandoffDialogOpen(true);
        logSystemAction("handoff_dialog_opened", { raw: trimmed });
        return;
      }

      if (isEmailFollowupCmd) {
        const args = trimmed.replace(/\/email_?followup\s*/i, "");
        const note = args || "Email follow-up requested by consultant.";
        handleFollowup(note);
        logSystemAction("email_followup", { note, raw: trimmed });
        return;
      }

      if (isFollowupCmd) {
        const args = trimmed.replace(/\/follow-?up\s*/i, "");
        const note = args || "Follow-up requested by consultant.";
        handleFollowup(note);
        logSystemAction("followup", { note, raw: trimmed });
        return;
      }

      if (isSendQuoteCmd) {
        logSystemAction("send_quote", { raw: trimmed });
        if (inquiry?.id) {
          updateDoc(doc(firestoreDb, "allInquiries", inquiry.id), {
            lastQuoteCommandAt: serverTimestamp(),
          }).catch(console.error);
        }
        try { window.open(QUOTE_URL, "_blank", "noopener"); } catch (e) { console.error(e); }
        return;
      }

      // ✅ removed entire /ask handler block

      if (isGhostCmd) {
        const ghostText = trimmed.replace(/^\/ghost\w*/i, "").trim();
        if (ghostText) handleSendMessage(ghostText);
        logSystemAction("ghost_writing", { raw: trimmed, textSent: ghostText || null });
        return;
      }

      if (trimmed.startsWith("/")) {
        logSystemAction("unknown_command", { raw: trimmed });
      }
      handleSendMessage(trimmed);
    }
  };

  const handleSentimentChange = (sent) => {
    setCurrentMood(sent || { label: "neutral", score: 0 });
  };

  const handleAutoUrgent = () => {
    if (isChatClosed) return;
    markAsUrgent({ automatic: true });
  };

  const moodDisplay = useMemo(() => {
    switch (currentMood.label) {
      case "positive": return { emoji: "😊", text: "Positive" };
      case "negative": return { emoji: "😟", text: "Frustrated" };
      default: return { emoji: "😐", text: "Neutral" };
    }
  }, [currentMood]);

  const customerDetailsArray = useMemo(() => {
    if (!inquiry) return [];
    const details = [];
    if (inquiry.inquiryUserType) {
      details.push({
        label: "User Type",
        value: isServiceProvider ? "Service Provider" : "Customer",
        isHighlight: true,
      });
    }
    if (inquiry.formData) {
      const { fullName, email, phone, ...otherData } = inquiry.formData;
      details.push({ label: "Full Name", value: fullName });
      details.push({ label: "Email", value: email });
      details.push({ label: "Phone", value: phone });

      Object.entries(otherData).forEach(([key, value]) => {
        if (!value) return;
        const formattedKey = key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        details.push({ label: formattedKey, value });
      });
    }
    return details.filter((d) => d.value);
  }, [inquiry, isServiceProvider]);

  // --- NEW HELPER: Get Dynamic Status Text ---
  const getClosedStatusText = () => {
    if (inquiry?.status === "closed_by_user") return "Chat closed by Customer";
    if (inquiry?.status === "closed_by_provider") return "Chat closed by Service Provider";

    const closerName = inquiry?.closedByName || inquiry?.consultantName || "Consultant";
    return `Chat closed by ${closerName}`;
  };

  if (!inquiry) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>No inquiry selected.</Typography>
      </Paper>
    );
  }

  const hasFollowup = !!inquiry.followupRequested;

  return (
    <Paper
      sx={{
        p: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
          gap: 1
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden", flexGrow: 1, minWidth: 200 }}>
          <Tooltip title="Back to List">
            <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            invisible={!computedOnline}
            sx={{
              mr: 1.5,
              "& .MuiBadge-badge": {
                backgroundColor: '#44b700',
                color: '#44b700',
                boxShadow: "0 0 0 2px white",
                animation: "pulse 1.5s infinite"
              },
              "@keyframes pulse": {
                "0%": { transform: "scale(.8)", opacity: 1 },
                "100%": { transform: "scale(2.4)", opacity: 0 }
              }
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: isServiceProvider ? "secondary.main" : "primary.light",
              }}
            >
              {isServiceProvider ? <BusinessIcon fontSize="small" /> : inquiry.formData?.fullName?.charAt(0) || "C"}
            </Avatar>
          </Badge>

          <Box sx={{ overflow: "hidden", display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2, fontSize: '1rem' }}>
              {inquiry.formData?.fullName || inquiry.full_name || "Customer"}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: computedOnline ? 'success.main' : 'text.secondary',
                fontWeight: computedOnline ? 600 : 400,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {computedOnline ? (
                "● Online"
              ) : (
                <>
                  <AccessTimeIcon sx={{ fontSize: 12, opacity: 0.7 }} />
                  {displayLastSeen ? `Last seen ${getRelativeTime(displayLastSeen)}` : "Offline"}
                </>
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            size="small"
            label={isMobile ? moodDisplay.emoji : `${moodDisplay.emoji} ${moodDisplay.text}`}
            sx={{
              bgcolor: "rgba(0,0,0,0.03)",
              fontWeight: 500,
              pointerEvents: "none"
            }}
          />

          {isHot && (
            <Tooltip title="Hot Lead">
              <WhatshotIcon
                sx={{
                  fontSize: '1.5rem',
                  color: '#ffd600',
                  animation: 'flashYellow 1.2s infinite'
                }}
              />
            </Tooltip>
          )}

          {isUrgent && (
            <Chip
              size="small"
              color="error"
              label={isMobile ? "⚡" : "⚡ Priority"}
              sx={{
                fontWeight: 600,
                pointerEvents: "none",
                minWidth: isMobile ? 'auto' : 80
              }}
            />
          )}

          {hasFollowup && (
            <Tooltip title="Schedule on Google Calendar">
              <Chip
                size="small"
                color="warning"
                label={isMobile ? "📅" : "📅 Follow-up"}
                sx={{ fontWeight: 600, cursor: "pointer" }}
                onClick={() => {
                  const title = encodeURIComponent(`Follow up: ${inquiry.formData?.fullName || 'Customer'}`);
                  const details = encodeURIComponent(`Inquiry ID: ${inquiry.id}\nNote: ${inquiry.followupNote || ''}`);
                  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}`;
                  window.open(url, '_blank');
                }}
              />
            </Tooltip>
          )}

          <Divider orientation="vertical" flexItem sx={{ height: 20, mx: 0.5 }} />

          <Tooltip
            title={isCustomerDetailsVisible ? "Hide Details" : "Show Details"}
          >
            <IconButton
              onClick={() =>
                setIsCustomerDetailsVisible(!isCustomerDetailsVisible)
              }
              size="small"
            >
              {isCustomerDetailsVisible ? (
                <VisibilityOffIcon />
              ) : (
                <VisibilityIcon />
              )}
            </IconButton>
          </Tooltip>
          {!isChatClosed && (
            <Tooltip title="Mark Inquiry as Closed">
              <IconButton
                onClick={() => onMarkClosed(inquiry)}
                size="small"
                color="primary"
                sx={{ ml: 0.5 }}
              >
                <CheckCircleOutline />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {showTips && (
        <Box
          sx={{
            position: "absolute",
            bottom: 96,
            left: { xs: 8, sm: 24 },
            zIndex: 5,
            maxWidth: { xs: "90%", sm: 420 },
          }}
        >
          <Paper
            sx={{
              p: 1.5,
              borderRadius: 3,
              boxShadow: 6,
              bgcolor: "#1e88e5",
              color: "#ffffff",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, opacity: 0.9 }}
            >
              Quick tip
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mt: 0.25, mb: 0.5 }}
            >
              Tip {tipIndex + 1} of {CHAT_TIPS.length}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", opacity: 0.95 }}
            >
              {CHAT_TIPS[tipIndex]}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => setShowTips(false)}
                sx={{ color: "#e3f2fd" }}
              >
                Dismiss
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{
                  bgcolor: "#0f6ad8",
                  "&:hover": { bgcolor: "#0b56ac" },
                }}
                onClick={() => {
                  if (tipIndex < CHAT_TIPS.length - 1) {
                    setTipIndex((prev) => prev + 1);
                  } else {
                    setShowTips(false);
                  }
                }}
              >
                {tipIndex < CHAT_TIPS.length - 1 ? "Next" : "Got it"}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      <Box sx={{ flexShrink: 0 }}>
        <Collapse in={isCustomerDetailsVisible} timeout="auto" unmountOnExit>
          <Paper
            square
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              maxHeight: "none",
              overflow: "visible",
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
              <PeopleAltIcon fontSize="small" color="disabled" sx={{ mr: 1, fontSize: "1.1rem" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                Customer Details
              </Typography>
            </Box>

            {customerDetailsArray.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "1fr 1fr 1fr",
                    md: "repeat(4, 1fr)"
                  },
                  gap: 1.5,
                  width: "100%",
                }}
              >
                {customerDetailsArray.map((detail) => (
                  <CustomerDetailItem key={detail.label} {...detail} />
                ))}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                No additional details provided.
              </Typography>
            )}
          </Paper>
        </Collapse>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 2,
          bgcolor: "background.default",
        }}
      >
        {loadingMessages ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : messageFetchError && messages.length === 0 ? (
          <Alert severity="error">{messageFetchError}</Alert>
        ) : messages.length === 0 ? (
          <Box sx={{ p: 3 }}>
            {!inquiry?.userId ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                This inquiry was submitted via a form or email — no live chat session exists.
              </Alert>
            ) : (
              <Typography sx={{ textAlign: "center", p: 2, fontStyle: "italic" }}>
                {isChatClosed ? "No messages in this closed chat." : "No messages yet."}
              </Typography>
            )}
            {(!inquiry?.userId) && (
              <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {(inquiry.formData?.fullName || inquiry.full_name) && <Box><Typography variant="caption" color="text.secondary">Name</Typography><Typography variant="body2" fontWeight={600}>{inquiry.formData?.fullName || inquiry.full_name}</Typography></Box>}
                {(inquiry.formData?.email || inquiry.email) && <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2" fontWeight={600}>{inquiry.formData?.email || inquiry.email}</Typography></Box>}
                {(inquiry.formData?.phone || inquiry.phone) && <Box><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2" fontWeight={600}>{inquiry.formData?.phone || inquiry.phone}</Typography></Box>}
                {inquiry.status && <Box><Typography variant="caption" color="text.secondary">Status</Typography><Typography variant="body2" fontWeight={600}>{inquiry.status}</Typography></Box>}
                {inquiry.created_at && <Box><Typography variant="caption" color="text.secondary">Submitted</Typography><Typography variant="body2" fontWeight={600}>{new Date(inquiry.created_at).toLocaleDateString()}</Typography></Box>}
              </Box>
            )}
          </Box>
        ) : (
          messages.map((msg) => {
            if (!msg.text && !msg.actionType) return null;

            const isSystem =
              (msg.sender || "").toString().toLowerCase() === "system";
            const fromConsultant = !isSystem && isConsultantMessage(msg);

            const alignment = isSystem
              ? "center"
              : fromConsultant
                ? "flex-end"
                : "flex-start";

            const bubbleBg = isSystem
              ? "grey.300"
              : fromConsultant
                ? "primary.main"
                : "grey.200";

            const bubbleColor = isSystem
              ? "text.primary"
              : fromConsultant
                ? "primary.contrastText"
                : "text.primary";

            const borderRadius = isSystem
              ? "16px"
              : fromConsultant
                ? "12px 12px 0 12px"
                : "12px 12px 12px 0";

            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: alignment,
                  mb: 1.5,
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    maxWidth: "75%",
                    width: "fit-content"
                  }}
                >
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius,
                      bgcolor: bubbleBg,
                      color: bubbleColor,
                      textAlign: isSystem ? "center" : "left",
                      fontSize: isSystem ? "0.75rem" : "0.875rem",
                      px: isSystem ? 1.5 : 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {msg.text}
                    </Typography>
                    {!isSystem && (
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          textAlign: "right",
                          fontSize: "0.7rem",
                          opacity: 0.8,
                          mt: 0.5,
                        }}
                      >
                        {msg.senderName ||
                          (fromConsultant
                            ? consultantDisplayName
                            : "Customer")}{" "}
                        {" - "}
                        {msg.timestamp?.toDate
                          ? msg.timestamp
                            .toDate()
                            .toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </Typography>
                    )}
                  </Box>
                </motion.div>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Dialog
        open={handoffDialogOpen && !isChatClosed}
        onClose={() => setHandoffDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Hand off inquiry</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Where would you like to transfer this chat?
          </DialogContentText>

          {availableConsultants.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                DIRECT TRANSFER
              </Typography>
              <List dense>
                {availableConsultants.map(colleague => (
                  <ListItemButton
                    key={colleague.id}
                    onClick={() => {
                      setHandoffDialogOpen(false);
                      handleHandoff("specific", colleague);
                    }}
                    sx={{ border: '1px solid #eee', borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                        {colleague.name?.charAt(0)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText primary={colleague.name} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}

          {marketingUsers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                MARKETING TEAM
              </Typography>
              <List dense>
                {marketingUsers.map(user => (
                  <ListItemButton
                    key={user.id}
                    onClick={() => {
                      setHandoffDialogOpen(false);
                      handleHandoff("specific", user);
                    }}
                    sx={{ border: '1px solid #eee', borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'warning.main' }}>
                        {user.name?.charAt(0)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText primary={user.name} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 1 }}>OR</Divider>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={() => {
                setHandoffDialogOpen(false);
                handleHandoff("marketing");
              }}
            >
              Marketing Team
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHandoffDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          mt: "auto",
          p: 1.5,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        {isChatClosed ? (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
              {getClosedStatusText()}
            </Typography>
            {inquiry?.closedAt && (
              <Typography variant="caption" color="text.secondary">
                {inquiry.closedAt?.toDate ? new Date(inquiry.closedAt.toDate()).toLocaleString() : new Date(inquiry.closedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        ) : (
          <OmniCommandBar
            messages={messages}
            customerName={
              inquiry.formData?.fullName ||
              (isServiceProvider ? "Service Provider" : "Customer")
            }
            onExecute={handleOmniExecute}
            onSentimentChange={handleSentimentChange}
            onAutoUrgent={handleAutoUrgent}
          />
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
